import {
    SESSION_KEYS,
    IMPERSONATION_BACKUP_KEY,
    IMPERSONATION_STATE_KEY,
    USER_LOCALSTORAGE_KEY,
    PER_USER_CACHE_LOCAL_PREFIXES,
    PER_USER_CACHE_LOCAL_EXACT,
    PER_USER_CACHE_SESSION_PREFIXES,
} from "@/shared/config/localstorage";
import type { User } from "@/shared/lib/context/UserContext";
import { finalizeEmailAuth } from "@/features/auth/lib/storeSessionTokens";
import { resetIdentityCache } from "@/features/identity/api/identityApi";
import { activityLog } from "@/features/activityLog";
import type { ImpersonationResult } from "../api";

const R2_WORKER_URL = "https://bucket.torsunovoleggen.workers.dev";
const PERSONAL_DATA_WORKER_URL =
    "https://personal-data-worker.torsunovoleggen.workers.dev";

export interface ImpersonationState {
    active: boolean;
    targetEmail: string;
    targetName: string;
    actorEmail: string;
    expiresAt: number; // epoch ms
    startedAt: number; // epoch ms
}

type SetUser = (user: User | string) => void;

// ---- helpers ----------------------------------------------------------------

function snapshotSession(): Record<string, string | null> {
    const snap: Record<string, string | null> = {};
    for (const k of SESSION_KEYS) snap[k] = localStorage.getItem(k);
    return snap;
}

function scrubPerUserCaches(): void {
    try {
        const drop: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
            const k = localStorage.key(i);
            if (!k) continue;
            if (
                PER_USER_CACHE_LOCAL_PREFIXES.some((p) => k.startsWith(p)) ||
                PER_USER_CACHE_LOCAL_EXACT.includes(k)
            ) {
                drop.push(k);
            }
        }
        drop.forEach((k) => localStorage.removeItem(k));
    } catch {
        /* best effort */
    }
    try {
        const drop: string[] = [];
        for (let i = 0; i < sessionStorage.length; i++) {
            const k = sessionStorage.key(i);
            if (k && PER_USER_CACHE_SESSION_PREFIXES.some((p) => k.startsWith(p))) {
                drop.push(k);
            }
        }
        drop.forEach((k) => sessionStorage.removeItem(k));
    } catch {
        /* best effort */
    }
}

// Same-tab signal the banner listens for (storage events fire only in OTHER
// tabs, so the initiating tab needs this to show/hide the banner immediately).
export const IMPERSONATION_CHANGED_EVENT = "impersonation-changed";

function pulse(): void {
    try {
        window.dispatchEvent(new Event("course-purchase-finalized"));
    } catch {
        /* ignore */
    }
    try {
        window.dispatchEvent(new Event(IMPERSONATION_CHANGED_EVENT));
    } catch {
        /* ignore */
    }
    try {
        localStorage.setItem("course_purchase_pulse", String(Date.now()));
    } catch {
        /* ignore */
    }
}

function parseUser(raw: string | null): User | string {
    if (!raw) return "";
    try {
        return JSON.parse(raw) as User;
    } catch {
        return raw;
    }
}

async function revokeToken(workerUrl: string, token: string | null): Promise<void> {
    if (!token) return;
    try {
        await fetch(`${workerUrl}/auth/revoke`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ session_token: token }),
        });
    } catch {
        /* best effort — short TTL is the backstop */
    }
}

// ---- public API -------------------------------------------------------------

export function readImpersonationState(): ImpersonationState | null {
    try {
        const raw = localStorage.getItem(IMPERSONATION_STATE_KEY);
        return raw ? (JSON.parse(raw) as ImpersonationState) : null;
    } catch {
        return null;
    }
}

export function isImpersonating(): boolean {
    return !!readImpersonationState()?.active;
}

/**
 * Snapshot the admin's session, scrub cross-user caches, install the target's
 * read-only session via finalizeEmailAuth (which exchanges the imp-marked
 * email_id_token at both workers — each stamps `impersonatedBy`), and swap the
 * React user → target. Throws `already_impersonating` (nested guard).
 */
export async function enterImpersonation(
    session: ImpersonationResult,
    actorEmail: string,
    setUser: SetUser,
): Promise<void> {
    if (isImpersonating() || localStorage.getItem(IMPERSONATION_BACKUP_KEY)) {
        throw new Error("already_impersonating");
    }

    // 0. The admin's own activity log gets the start and its last batch;
    //    nothing is recorded while viewing as the target.
    await activityLog.suspendForImpersonation(session.email);

    // 1. Snapshot the admin's full session under one backup key.
    localStorage.setItem(
        IMPERSONATION_BACKUP_KEY,
        JSON.stringify(snapshotSession()),
    );

    // 2. Scrub per-user caches so the admin's Q history / progress don't bleed in.
    scrubPerUserCaches();

    // 3. Install the target session. finalizeEmailAuth exchanges the imp token
    //    at the bucket + personal-data workers (both stamp impersonatedBy), then
    //    writes r2/personal_data/site tokens + email_id_token + swaps the user.
    //    No site_session_token for impersonation (read-only view doesn't need
    //    a site_server-authed session; entitlement reads are worker/email-based).
    await finalizeEmailAuth(
        {
            email_id_token: session.email_id_token,
            site_session_token: "",
            email: session.email,
            name: session.name || "",
        },
        { setUser },
    );

    // 4. Write the sentinel (drives the banner + cross-tab switch).
    const expiresAt = session.expiresAt
        ? Date.parse(session.expiresAt)
        : Date.now() + 15 * 60 * 1000;
    const state: ImpersonationState = {
        active: true,
        targetEmail: session.email,
        targetName: session.name || session.email,
        actorEmail,
        expiresAt,
        startedAt: Date.now(),
    };
    localStorage.setItem(IMPERSONATION_STATE_KEY, JSON.stringify(state));

    // 5. Refetch entitlements as the target.
    pulse();
}

/**
 * Revoke the impersonation worker sessions, clear the cached email_id_token
 * (so the boot ensureR2SessionToken hook can't revive it), restore the admin's
 * snapshot, and swap the React user back. Idempotent / double-exit safe.
 */
export async function exitImpersonation(setUser: SetUser): Promise<void> {
    // Revoke the impersonation sessions on both workers (shared KV) so the
    // tokens die immediately, not just at TTL.
    const r2 = localStorage.getItem("r2_session_token");
    const pd = localStorage.getItem("personal_data_session_token");
    await Promise.allSettled([
        revokeToken(R2_WORKER_URL, r2),
        revokeToken(PERSONAL_DATA_WORKER_URL, pd),
    ]);

    const raw = localStorage.getItem(IMPERSONATION_BACKUP_KEY);
    scrubPerUserCaches();

    let restoredUser: User | string | null = null;
    if (raw) {
        try {
            const backup = JSON.parse(raw) as Record<string, string | null>;
            for (const k of SESSION_KEYS) {
                const v = backup[k];
                if (v === null || v === undefined) localStorage.removeItem(k);
                else localStorage.setItem(k, v);
            }
            restoredUser = parseUser(backup[USER_LOCALSTORAGE_KEY] ?? null);
        } catch {
            /* fall through */
        }
    }

    localStorage.removeItem(IMPERSONATION_BACKUP_KEY);
    localStorage.removeItem(IMPERSONATION_STATE_KEY);
    activityLog.resumeAfterImpersonation();

    // The tab cached the TARGET's identity while impersonating; the admin's
    // own session is back now and must not wear the target's name.
    resetIdentityCache();

    if (restoredUser === null) {
        restoredUser = parseUser(localStorage.getItem(USER_LOCALSTORAGE_KEY));
    }

    setUser(restoredUser);
    pulse();
}

/**
 * Reconstruct the user for a tab that saw an impersonation state change via a
 * `storage` event. Target while active, else the (restored) current user.
 */
export function userForCurrentState(): User | string {
    const s = readImpersonationState();
    if (s?.active) {
        return {
            id: `email:${s.targetEmail}`,
            email: s.targetEmail,
            name: s.targetName || s.targetEmail,
        };
    }
    return parseUser(localStorage.getItem(USER_LOCALSTORAGE_KEY));
}
